import React, { useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Alert,
  StatusIndicator,
  Badge,
  Select,
  Button
} from '@cloudscape-design/components'
import { apiService } from '../services/api'
import { ColorTheme } from '../types/themes'
import { getDefaultTheme } from '../services/themeApi'
import { themeCache } from '../services/themeCache'
import { decodeEphemeralLocations } from '../utils/ephemeralLocations'
import { fetchAuthSession } from 'aws-amplify/auth'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Route params
export interface PreviewRouteParams {
  userId: string    // "ephemeral" for ephemeral mode, or Cognito user sub
  dataProduct: string
}

// Decoded ephemeral location
export interface EphemeralLocation {
  id: string
  name: string
  lat: number
  lng: number
}

const dataProductDisplayNames: Record<string, string> = {
  metar: 'METAR Weather',
  marine: 'Marine Conditions',
  trail: 'Trail Conditions',
  nws: 'NWS Observations',
  hf_propagation: 'HF Radio Propagation',
  beach_conditions: 'Beach Conditions'
}

const NEUTRAL_COLOR = '#9ca3af'

// Attribute options per data product (reused from MapVisualization.tsx)
export const attributeOptions: Record<string, Array<{ label: string; value: string }>> = {
  metar: [
    { label: 'Flight Category', value: 'flightCategory' },
    { label: 'Wind Speed', value: 'windSpeed' },
    { label: 'Temperature', value: 'temperature' },
    { label: 'Visibility', value: 'visibility' }
  ],
  marine: [
    { label: 'Wave Height', value: 'waveHeight' },
    { label: 'Wind Speed', value: 'windSpeed' },
    { label: 'Water Temperature', value: 'waterTemperature' },
    { label: 'Air Temperature', value: 'airTemperature' },
    { label: 'Air Pressure', value: 'airPressure' },
    { label: 'Wave Period', value: 'wavePeriod' },
    { label: 'Wind Direction', value: 'windDirection' }
  ],
  trail: [
    { label: 'Trail Condition', value: 'condition' },
    { label: 'Snow Depth', value: 'snowDepth' },
    { label: 'Temperature', value: 'temperature' }
  ],
  nws: [
    { label: 'Temperature', value: 'temperature' },
    { label: 'Wind Speed', value: 'windSpeed' },
    { label: 'Relative Humidity', value: 'relativeHumidity' },
    { label: 'Visibility', value: 'visibility' },
    { label: 'Wind Direction', value: 'windDirection' }
  ],
  hf_propagation: [
    { label: '160m (1.8 MHz)', value: 'band_160m' },
    { label: '80m (3.5 MHz)', value: 'band_80m' },
    { label: '40m (7.0 MHz)', value: 'band_40m' },
    { label: '30m (10.1 MHz)', value: 'band_30m' },
    { label: '20m (14.0 MHz)', value: 'band_20m' },
    { label: '17m (18.1 MHz)', value: 'band_17m' },
    { label: '15m (21.0 MHz)', value: 'band_15m' },
    { label: '10m (28.0 MHz)', value: 'band_10m' }
  ],
  beach_conditions: [
    { label: 'Swim Risk', value: 'swimRisk' },
    { label: 'Rip Current', value: 'ripCurrent' },
    { label: 'Surf Height', value: 'surfHeight' },
    { label: 'Water Temperature', value: 'waterTemperature' },
    { label: 'UV Index', value: 'uvIndex' },
    { label: 'Thunderstorm', value: 'thunderstorm' }
  ]
}

const PreviewMap: React.FC = () => {
  const { userId, dataProduct } = useParams<Record<string, string>>()
  const [searchParams] = useSearchParams()

  const isEphemeral = userId === 'ephemeral'

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [ownerName, setOwnerName] = useState<string>('')
  const [locations, setLocations] = useState<Array<{
    locationId: string
    name: string
    coordinates?: { latitude: number; longitude: number }
  }>>([])

  // Attribute dropdown state
  const [selectedAttribute, setSelectedAttribute] = useState<any>(null)
  const [attributeLoading, setAttributeLoading] = useState(false)
  const [attributeError, setAttributeError] = useState<string | null>(null)
  const [activeTheme, setActiveTheme] = useState<ColorTheme | null>(null)
  const [attributeData, setAttributeData] = useState<Record<string, any>>({})

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markersRef = useRef<L.CircleMarker[]>([])

  // Count only locations with valid coordinates
  const markerCount = locations.filter(
    loc => loc.coordinates &&
      typeof loc.coordinates.latitude === 'number' &&
      typeof loc.coordinates.longitude === 'number' &&
      loc.coordinates.latitude >= -90 && loc.coordinates.latitude <= 90 &&
      loc.coordinates.longitude >= -180 && loc.coordinates.longitude <= 180
  ).length

  const dataProductLabel = dataProduct ? (dataProductDisplayNames[dataProduct] || dataProduct) : ''

  useEffect(() => {
    if (isEphemeral) {
      // Ephemeral mode: decode locations from URL query parameter
      const locationsParam = searchParams.get('locations')
      const result = decodeEphemeralLocations(locationsParam)

      if (result.error) {
        setError(result.error)
        setLoading(false)
        return
      }

      setLocations(result.locations!)
      setLoading(false)
      return
    }

    if (!userId || !dataProduct) {
      setError('Invalid route parameters.')
      setLoading(false)
      return
    }

    loadLocationList()
  }, [userId, dataProduct])

  const loadLocationList = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiService.getUserLocationList(userId!, dataProduct!)

      if (response.statusCode === 200 && response.data) {
        const listData = response.data as any

        // Resolve owner name: check current session first, then fall back to users API
        let resolvedName = listData.username || listData.owner_name || ''
        if (!resolvedName || resolvedName === userId) {
          try {
            const session = await fetchAuthSession()
            const payload = session.tokens?.idToken?.payload as any
            if (payload?.sub === userId) {
              const givenName = payload.given_name || ''
              const familyName = payload.family_name || ''
              resolvedName = [givenName, familyName].filter(Boolean).join(' ') || payload.email?.split('@')[0] || ''
            }
          } catch { /* ignore session errors */ }

          // If still unresolved (viewing another user's list), try users API
          if (!resolvedName || resolvedName === userId) {
            try {
              const usersResponse = await apiService.getUsers()
              if (usersResponse.statusCode === 200 && usersResponse.data) {
                const users = (usersResponse.data as any).users || usersResponse.data || []
                const owner = users.find((u: any) => (u.user_id || u.userId || u.id) === userId)
                if (owner) {
                  const givenName = owner.given_name || owner.givenName || ''
                  const familyName = owner.family_name || owner.familyName || ''
                  resolvedName = [givenName, familyName].filter(Boolean).join(' ') || owner.email || ''
                }
              }
            } catch { /* ignore lookup errors */ }
          }
        }

        setOwnerName(resolvedName || 'Location List')
        setLocations(
          (listData.locations || []).map((loc: any) => ({
            locationId: loc.location_id || loc.locationId,
            name: loc.commonName || loc.common_name || loc.name || loc.location_id || loc.locationId,
            coordinates: loc.coordinates ? {
              latitude: Number(loc.coordinates.latitude),
              longitude: Number(loc.coordinates.longitude)
            } : undefined
          }))
        )
      } else if (response.statusCode === 404) {
        setError('Location list not found.')
      } else {
        setError(response.error || 'Failed to load location list.')
      }
    } catch (err) {
      console.error('Error loading location list:', err)
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle attribute selection: fetch data and theme
  const handleAttributeChange = async (selectedOption: any) => {
    // Allow clearing selection
    if (!selectedOption || !selectedOption.value) {
      setSelectedAttribute(null)
      setActiveTheme(null)
      setAttributeData({})
      setAttributeError(null)
      return
    }

    setSelectedAttribute(selectedOption)
    setAttributeLoading(true)
    setAttributeError(null)
    // Clear stale data so markers go neutral while loading
    setAttributeData({})

    try {
      // Fetch theme (check cache first, then API)
      let theme: ColorTheme | null = null
      try {
        const cachedTheme = themeCache.getTheme(dataProduct!, selectedOption.value)
        if (cachedTheme) {
          theme = cachedTheme
        } else {
          theme = await getDefaultTheme(dataProduct!, selectedOption.value)
          themeCache.setTheme(theme)
        }
        setActiveTheme(theme)
      } catch (themeErr) {
        console.error('Error fetching theme:', themeErr)
        // Fall back to grayscale theme (same pattern as MapVisualization.tsx)
        const fallbackTheme: ColorTheme = {
          theme_id: 'grayscale-fallback',
          theme_name: 'Grayscale (Fallback)',
          data_product: dataProduct!,
          attribute: selectedOption.value,
          theme_type: 'system',
          is_default: false,
          mapping_type: 'categorical',
          mappings: [
            { category: 'default', color: '#9ca3af', label: 'No Data' }
          ],
          version: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        setActiveTheme(fallbackTheme)
        theme = fallbackTheme
        setAttributeError('Failed to load color theme. Using default grayscale.')
      }

      // Fetch location data
      if (dataProduct === 'hf_propagation') {
        await loadHfPropagationAttributeData(selectedOption.value)
      } else {
        await loadAttributeData()
      }
    } catch (err) {
      console.error('Error loading attribute data:', err)
      setAttributeError('Failed to load location data. Markers retain default coloring.')
    } finally {
      setAttributeLoading(false)
    }
  }

  // Fetch attribute data for standard data products via getMapLocations
  const loadAttributeData = async () => {
    const validLocs = locations.filter(
      loc => loc.coordinates &&
        typeof loc.coordinates.latitude === 'number' &&
        typeof loc.coordinates.longitude === 'number'
    )

    if (validLocs.length === 0) return

    // Pass specific location IDs so the backend only fetches data for these locations
    // instead of scanning the entire bounding box (which times out for large areas)
    const locationIds = locations.map(loc => loc.locationId).filter(Boolean)

    const lats = validLocs.map(loc => loc.coordinates!.latitude)
    const lngs = validLocs.map(loc => loc.coordinates!.longitude)
    const padding = 1
    const minLat = Math.min(...lats) - padding
    const maxLat = Math.max(...lats) + padding
    const minLng = Math.min(...lngs) - padding
    const maxLng = Math.max(...lngs) + padding

    const response = await apiService.getMapLocations(dataProduct!, {
      minLat,
      maxLat,
      minLng,
      maxLng
    }, locationIds)

    if (response.statusCode === 200 && response.data) {
      const responseData = response.data as any
      if (responseData.status === 'success' && Array.isArray(responseData.locations)) {
        // Build a lookup map: locationId -> payload data
        const dataMap: Record<string, any> = {}
        responseData.locations.forEach((loc: any) => {
          const locId = loc.location_id || loc.locationId
          if (locId) {
            dataMap[locId] = loc.data?.payload || loc.payload || null
          }
        })
        setAttributeData(dataMap)
      } else {
        throw new Error(responseData.error || 'Invalid response format')
      }
    } else {
      throw new Error(response.error || 'Failed to load location data')
    }
  }

  // Fetch HF propagation data using existing loading pattern
  const loadHfPropagationAttributeData = async (attribute: string) => {
    // Fetch default HF locations for canonical names and coordinates
    const defaultsResponse = await apiService.getDefaultHfLocations()
    let defaultLocations: any[] = []

    if (defaultsResponse.statusCode === 200 && defaultsResponse.data) {
      const defaultsData = defaultsResponse.data as any
      if (defaultsData.status === 'success') {
        defaultLocations = defaultsData.locations || []
      }
    }

    // Fetch propagation data from the map API
    const response = await apiService.getMapLocations('hf_propagation', {
      minLat: -90,
      maxLat: 90,
      minLng: -180,
      maxLng: 180
    })

    const dataMap: Record<string, any> = {}

    if (response.statusCode === 200 && response.data) {
      const responseData = response.data as any
      if (responseData.status === 'success') {
        const hfLocations = responseData.locations || []
        hfLocations.forEach((loc: any) => {
          const locId = loc.location_id
          if (locId) {
            // Build payload with band data for theme coloring
            let attenuation: number | null = null
            if (loc.bands && Array.isArray(loc.bands)) {
              const bandName = attribute.replace('band_', '')
              const bandResult = loc.bands.find((b: any) => b.band === bandName)
              if (bandResult) {
                attenuation = bandResult.attenuation_db
              }
            }
            dataMap[locId] = {
              [attribute]: attenuation ?? loc.attenuation_db,
              _bands: loc.bands || [],
              _hfData: loc,
              _isHfPropagation: true
            }
          }
          // Also index by coordinate key for matching
          if (loc.coordinates) {
            const coordKey = `${loc.coordinates.latitude}_${loc.coordinates.longitude}`
            let attenuation: number | null = null
            if (loc.bands && Array.isArray(loc.bands)) {
              const bandName = attribute.replace('band_', '')
              const bandResult = loc.bands.find((b: any) => b.band === bandName)
              if (bandResult) {
                attenuation = bandResult.attenuation_db
              }
            }
            dataMap[coordKey] = {
              [attribute]: attenuation ?? loc.attenuation_db,
              _bands: loc.bands || [],
              _hfData: loc,
              _isHfPropagation: true
            }
          }
        })
      }
    }

    setAttributeData(dataMap)
  }

  // Initialize Leaflet map
  useEffect(() => {
    if (loading || error || !mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      center: [39.8, -98.5],
      zoom: 4,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      dragging: true
    })

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
      minZoom: 2
    }).addTo(map)

    mapRef.current = map

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [loading, error])

  // Plot circle markers when locations, attribute data, or theme change
  useEffect(() => {
    if (!mapRef.current) return

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    const validLocations = locations.filter(
      loc => loc.coordinates &&
        typeof loc.coordinates.latitude === 'number' &&
        typeof loc.coordinates.longitude === 'number' &&
        loc.coordinates.latitude >= -90 && loc.coordinates.latitude <= 90 &&
        loc.coordinates.longitude >= -180 && loc.coordinates.longitude <= 180
    )

    if (validLocations.length === 0) return

    const bounds = L.latLngBounds([])

    validLocations.forEach(location => {
      const latLng: L.LatLngExpression = [
        location.coordinates!.latitude,
        location.coordinates!.longitude
      ]

      // Determine marker color based on attribute selection and data
      let fillColor = NEUTRAL_COLOR
      if (selectedAttribute && activeTheme) {
        const payload = attributeData[location.locationId]
        if (payload) {
          const value = payload[selectedAttribute.value]
          if (value !== null && value !== undefined) {
            fillColor = themeCache.getColorForValue(activeTheme, value)
          }
        }
      }

      const marker = L.circleMarker(latLng, {
        radius: 8,
        fillColor,
        color: '#333',
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
      })

      // Build popup content with location identity and optional attribute value
      let popupContent = `
        <div style="font-family: sans-serif;">
          <strong>${location.name}</strong><br/>
          <small>${location.locationId}</small>`

      if (selectedAttribute) {
        const payload = attributeData[location.locationId]
        const value = payload ? payload[selectedAttribute.value] : undefined
        const displayValue = value !== null && value !== undefined ? String(value) : 'N/A'
        popupContent += `<br/><span style="color: #555;">${selectedAttribute.label}: <strong>${displayValue}</strong></span>`
      }

      popupContent += `</div>`

      marker.bindPopup(popupContent)

      marker.addTo(mapRef.current!)
      markersRef.current.push(marker)
      bounds.extend(latLng)
    })

    // Auto-fit map bounds with padding
    mapRef.current.fitBounds(bounds, { padding: [40, 40] })
  }, [locations, loading, error, selectedAttribute, activeTheme, attributeData])

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: 'xxl' }}>
          <StatusIndicator type="loading">Loading location list...</StatusIndicator>
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <SpaceBetween size="l">
        <Alert type="error">{error}</Alert>
        <Box>
          <Link to="/location-lists" style={{ textDecoration: 'none' }}>
            ← Back to Location Lists
          </Link>
        </Box>
      </SpaceBetween>
    )
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description={
          <SpaceBetween direction="horizontal" size="xs">
            <Badge color="blue">{dataProductLabel}</Badge>
            <Badge color="grey">{markerCount} marker{markerCount !== 1 ? 's' : ''}</Badge>
          </SpaceBetween>
        }
      >
        {isEphemeral ? 'Preview Map' : `${ownerName} — ${dataProductLabel}`}
      </Header>

      <Box>
        <Link to="/location-lists" style={{ textDecoration: 'none' }}>
          ← Back to Location Lists
        </Link>
      </Box>

      {isEphemeral && (
        <Alert type="info">
          Temporary Preview — locations not yet saved
        </Alert>
      )}

      {/* Attribute dropdown for color-coding */}
      {dataProduct && attributeOptions[dataProduct] && (
        <Container>
          <SpaceBetween size="s">
            <Box variant="awsui-key-label">Color by Attribute</Box>
            <SpaceBetween direction="horizontal" size="xs">
              <div style={{ flexGrow: 1, minWidth: '300px' }}>
                <Select
                  selectedOption={selectedAttribute}
                  onChange={({ detail }) => handleAttributeChange(detail.selectedOption)}
                  options={attributeOptions[dataProduct] || []}
                  placeholder={attributeLoading ? "Loading attribute data..." : "Select an attribute to color-code markers"}
                  disabled={attributeLoading}
                  data-testid="attribute-select"
                />
              </div>
              {selectedAttribute && (
                <Button
                  variant="normal"
                  onClick={() => handleAttributeChange(null)}
                  disabled={attributeLoading}
                >
                  Clear
                </Button>
              )}
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}

      {attributeError && (
        <Alert type="warning" dismissible onDismiss={() => setAttributeError(null)}>
          {attributeError}
        </Alert>
      )}

      {/* Leaflet map container */}
      <Container>
        <div
          ref={mapContainerRef}
          style={{
            height: '500px',
            borderRadius: '8px'
          }}
        />
      </Container>

      {/* Color legend — only shown when an attribute is selected */}
      {selectedAttribute && activeTheme && (
        <Container header={<Header variant="h2">Color Legend</Header>}>
          <SpaceBetween size="s">
            {themeCache.getLegendItems(activeTheme).map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: item.color,
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  )
}

export default PreviewMap
